import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import './CaseManagement.css';

const PartyDetails = () => {
  const { cases, setCases, advocates, addAuditLog } = useApp();
  const [selectedCaseId, setSelectedCaseId] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [partyType, setPartyType] = useState('Plaintiff');
  const [formData, setFormData] = useState({
    partyName: '',
    partyType: 'Plaintiff',
    address: '',
    contactNumber: '',
    email: '',
    advocateId: '',
  });
  const [editingId, setEditingId] = useState(null);

  const selectedCase = cases.find(c => c.id === selectedCaseId);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!selectedCaseId) {
      alert('Please select a case first');
      return;
    }

    const advocate = advocates.find(a => a.id === formData.advocateId);
    
    const party = {
      id: editingId || Date.now().toString(),
      ...formData,
      advocateName: advocate?.advocateName || '',
    };

    if (editingId) {
      setCases(cases.map(c => 
        c.id === selectedCaseId
          ? {
              ...c,
              parties: c.parties ? c.parties.map(p => 
                p.id === editingId ? party : p
              ) : [party]
            }
          : c
      ));
      addAuditLog('Party Updated', `Party ${formData.partyName} updated in case ${selectedCase?.caseNumber}`);
    } else {
      setCases(cases.map(c => 
        c.id === selectedCaseId
          ? {
              ...c,
              parties: [...(c.parties || []), party]
            }
          : c
      ));
      addAuditLog('Party Added', `Party ${formData.partyName} added to case ${selectedCase?.caseNumber}`);
    }
    
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      partyName: '',
      partyType: 'Plaintiff',
      address: '',
      contactNumber: '',
      email: '',
      advocateId: '',
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (party) => {
    setFormData(party);
    setPartyType(party.partyType);
    setEditingId(party.id);
    setShowForm(true);
  };

  const handleDelete = (partyId) => {
    setCases(cases.map(c => 
      c.id === selectedCaseId
        ? {
            ...c,
            parties: (c.parties || []).filter(p => p.id !== partyId)
          }
        : c
    ));
    addAuditLog('Party Deleted', `Party deleted from case ${selectedCase?.caseNumber}`);
  };

  const parties = selectedCase?.parties || [];

  return (
    <div className="case-container">
      <div className="case-header">
        <h1>🧑‍⚖️ Party Details</h1>
      </div>

      <div className="case-form-card">
        <h2>Select Case</h2>
        <div className="form-group">
          <select
            value={selectedCaseId}
            onChange={(e) => {
              setSelectedCaseId(e.target.value);
              setShowForm(false);
              resetForm();
            }}
            className="case-select"
          >
            <option value="">Select a case</option>
            {cases.map(c => (
              <option key={c.id} value={c.id}>
                {c.caseNumber} - {c.caseTitle}
              </option>
            ))}
          </select>
        </div>
      </div>

      {selectedCaseId && (
        <>
          <div className="case-header">
            <h2>Case: {selectedCase?.caseNumber} - {selectedCase?.caseTitle}</h2>
            <button onClick={() => {
              setShowForm(!showForm);
              setFormData({...formData, partyType: partyType});
            }} className="btn-primary">
              {showForm ? 'Cancel' : '+ Add Party'}
            </button>
          </div>

          {showForm && (
            <div className="case-form-card">
              <h2>{editingId ? 'Edit Party' : 'Add New Party'}</h2>
              <form onSubmit={handleSubmit}>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Party Type *</label>
                    <select
                      value={formData.partyType}
                      onChange={(e) => {
                        setFormData({...formData, partyType: e.target.value});
                        setPartyType(e.target.value);
                      }}
                      required
                    >
                      <option value="Plaintiff">Plaintiff / Petitioner</option>
                      <option value="Defendant">Defendant / Respondent</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Party Name *</label>
                    <input
                      type="text"
                      value={formData.partyName}
                      onChange={(e) => setFormData({...formData, partyName: e.target.value})}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Advocate</label>
                    <select
                      value={formData.advocateId}
                      onChange={(e) => setFormData({...formData, advocateId: e.target.value})}
                    >
                      <option value="">Select Advocate</option>
                      {advocates.map(advocate => (
                        <option key={advocate.id} value={advocate.id}>
                          {advocate.advocateName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Contact Number</label>
                    <input
                      type="tel"
                      value={formData.contactNumber}
                      onChange={(e) => setFormData({...formData, contactNumber: e.target.value})}
                    />
                  </div>

                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                    />
                  </div>

                  <div className="form-group full-width">
                    <label>Address</label>
                    <textarea
                      value={formData.address}
                      onChange={(e) => setFormData({...formData, address: e.target.value})}
                      rows="3"
                    />
                  </div>
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn-primary">
                    {editingId ? 'Update' : 'Add'} Party
                  </button>
                  <button type="button" onClick={resetForm} className="btn-secondary">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="case-table-card">
            <h2>Parties ({parties.length})</h2>
            {parties.length > 0 ? (
              <div className="parties-grid">
                <div className="party-section">
                  <h3>Plaintiff / Petitioner</h3>
                  {parties.filter(p => p.partyType === 'Plaintiff').map(party => (
                    <div key={party.id} className="party-card">
                      <div className="party-header">
                        <strong>{party.partyName}</strong>
                        <div>
                          <button onClick={() => handleEdit(party)} className="btn-edit-small">Edit</button>
                          <button onClick={() => handleDelete(party.id)} className="btn-delete-small">Delete</button>
                        </div>
                      </div>
                      <p><strong>Advocate:</strong> {party.advocateName || 'Not assigned'}</p>
                      <p><strong>Contact:</strong> {party.contactNumber || 'N/A'}</p>
                      <p><strong>Email:</strong> {party.email || 'N/A'}</p>
                      <p><strong>Address:</strong> {party.address || 'N/A'}</p>
                    </div>
                  ))}
                </div>

                <div className="party-section">
                  <h3>Defendant / Respondent</h3>
                  {parties.filter(p => p.partyType === 'Defendant').map(party => (
                    <div key={party.id} className="party-card">
                      <div className="party-header">
                        <strong>{party.partyName}</strong>
                        <div>
                          <button onClick={() => handleEdit(party)} className="btn-edit-small">Edit</button>
                          <button onClick={() => handleDelete(party.id)} className="btn-delete-small">Delete</button>
                        </div>
                      </div>
                      <p><strong>Advocate:</strong> {party.advocateName || 'Not assigned'}</p>
                      <p><strong>Contact:</strong> {party.contactNumber || 'N/A'}</p>
                      <p><strong>Email:</strong> {party.email || 'N/A'}</p>
                      <p><strong>Address:</strong> {party.address || 'N/A'}</p>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="empty-state">No parties added yet. Click "Add Party" to get started.</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default PartyDetails;

